import { useInfiniteQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { AppNav } from '@/components/AppNav';
import { fetchFeed, type Photo } from '@/lib/api';
import { useSession } from '@/hooks/useSession';
import { useLocale, useT } from '@/i18n/context';
import { groupByMonth } from '@/lib/months';

/**
 * The whole history as a grid, newest first, grouped by month.
 */
export function HistoryPage() {
  const { data, error, isPending, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ['photos', 'feed'],
      queryFn: ({ pageParam }) => fetchFeed(pageParam),
      initialPageParam: null as string | null,
      getNextPageParam: (last) => last.next_cursor,
    });

  const { data: user } = useSession();
  const { locale } = useLocale();
  const t = useT();
  const photos: Photo[] = data?.pages.flatMap((page) => page.photos) ?? [];
  // The real total, not how many pages have been scrolled into memory.
  const total = user?.streak.total_photos ?? photos.length;
  const groups = groupByMonth(photos, locale);

  const sentinel = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const node = sentinel.current;

    if (!node || !hasNextPage) {
      return;
    }

    // Loading on intersection rather than on a scroll handler: the observer
    // fires once per crossing instead of on every frame of the scroll.
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting) && !isFetchingNextPage) {
        void fetchNextPage();
      }
    });

    observer.observe(node);

    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col gap-6 p-6 pb-24">
      <header>
        <h1 className="text-3xl font-semibold tracking-tight">{t('history.title')}</h1>
        <p className="text-sm opacity-60">
          {total > 0 ? t('history.count', { n: total }) : t('history.subtitle_empty')}
        </p>
      </header>

      {isPending && <p className="text-sm opacity-60">{t('common.loading')}</p>}

      {error && (
        <p className="rounded-lg bg-red-500/10 p-4 text-sm text-red-500">
          {t('history.load_failed', { message: error.message })}
        </p>
      )}

      {!isPending && !error && photos.length === 0 && (
        <p className="rounded-lg bg-current/5 p-4 text-sm opacity-70">
          {t('history.empty')}
        </p>
      )}

      {groups.map((group) => (
        <section key={group.key} className="flex flex-col gap-2">
          <h2 className="text-sm font-medium opacity-70">{group.label}</h2>

          <ul className="grid grid-cols-3 gap-1.5">
            {group.photos.map((photo) => (
              <li key={photo.id}>
                <figure className="aspect-4/5 overflow-hidden rounded-lg bg-current/5">
                  {photo.thumb_url && (
                    <img
                      src={photo.thumb_url}
                      alt={photo.date}
                      // Only a few of these are ever on screen; the rest are
                      // signed URLs that need not be fetched until scrolled to.
                      loading="lazy"
                      decoding="async"
                      className="size-full object-cover"
                    />
                  )}
                </figure>
              </li>
            ))}
          </ul>
        </section>
      ))}

      <div ref={sentinel} aria-hidden="true" className="h-px" />

      {isFetchingNextPage && (
        <p className="text-center text-sm opacity-60">{t('history.loading_more')}</p>
      )}

      <AppNav />
    </main>
  );
}
